import { useTranslation } from 'react-i18next';
import { Star } from 'lucide-react';
import { cn } from '@shripos/ui';
import { FoodTypeMark } from '../../components/FoodTypeMark.jsx';
import { API_ORIGIN } from '../../lib/api.js';
import { fromPrice } from '../../lib/catalog.js';
import { rupees } from '../../lib/money.js';

export function CategoryRail({ categories, active, onSelect, hasFavorites }) {
  const { t } = useTranslation();
  const btn = (key, label, color) => (
    <button
      key={key}
      onClick={() => onSelect(key)}
      className={cn(
        'shrink-0 rounded-xl px-4 py-2.5 text-left text-sm font-semibold whitespace-nowrap transition md:w-full',
        active === key ? 'bg-slate-900 text-white' : 'bg-white text-slate-700 ring-1 ring-slate-200 hover:bg-slate-50',
      )}
      style={active !== key && color ? { boxShadow: `inset 4px 0 0 ${color}` } : undefined}
    >
      {label}
    </button>
  );
  return (
    <nav
      className="flex gap-2 overflow-x-auto pb-1 md:flex-col md:overflow-y-auto md:pb-0"
      aria-label={t('pos.categories', 'Categories')}
    >
      {hasFavorites &&
        btn(
          'favorites',
          <span className="inline-flex items-center gap-1.5">
            <Star className="size-4" /> {t('pos.favorites', 'Favourites')}
          </span>,
        )}
      {categories.map((c) => btn(c.id, c.name, c.color))}
      {btn('all', t('pos.allItems', 'All items'))}
    </nav>
  );
}

export function ItemTile({ item, unavailable, reason, onAdd, showImages }) {
  const { t } = useTranslation();
  const price = fromPrice(item);
  return (
    <button
      onClick={() => onAdd(item)}
      disabled={unavailable}
      className={cn(
        'group relative flex min-h-24 flex-col overflow-hidden rounded-xl bg-white text-left shadow-xs ring-1 ring-slate-200 transition',
        unavailable ? 'cursor-not-allowed opacity-50' : 'hover:ring-brand-400 active:scale-[0.98]',
      )}
      data-testid="item-tile"
    >
      {showImages && item.thumbUrl && (
        <img src={`${API_ORIGIN}${item.thumbUrl}`} alt="" loading="lazy" className="h-20 w-full object-cover" />
      )}
      <div className="flex flex-1 flex-col p-2.5">
        <div className="flex items-start gap-1.5">
          <FoodTypeMark type={item.foodType} className="mt-0.5 size-3.5" />
          <span className="line-clamp-2 text-sm leading-tight font-semibold text-slate-900">{item.name}</span>
        </div>
        <div className="mt-auto flex items-end justify-between pt-1.5">
          <span className="text-sm font-bold text-slate-800 tabular-nums">
            {item.variants?.length > 1 && (
              <span className="mr-0.5 text-xs font-normal text-slate-500">{t('pos.from', 'from')}</span>
            )}
            {rupees(price)}
          </span>
          {item.shortCode && <span className="font-mono text-[10px] text-slate-400">{item.shortCode}</span>}
        </div>
      </div>
      {unavailable && (
        <span className="absolute top-1.5 right-1.5 rounded bg-slate-900/80 px-1.5 py-0.5 text-[10px] font-semibold text-white">
          {reason}
        </span>
      )}
    </button>
  );
}
